'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

const HEADING_SELECTOR = 'main h1';
const WAIT_FOR_HEADING_MS = 3000;

// The new step already placed focus (e.g. an autoFocus input), so don't steal it
const stepHasFocus = () => {
  const active = document.activeElement;
  return !!active && active !== document.body && !!active.closest('main');
};

const focusHeading = (heading: HTMLElement) => {
  if (stepHasFocus()) return;
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  // Programmatic target only, not an interactive control: no focus ring
  heading.style.outline = 'none';
  heading.focus({ preventScroll: true });
};

/**
 * Moves focus to the step's main heading when the route changes (not on first mount),
 * so keyboard and screen reader users land on the new step instead of a stale control.
 * Skipped when the new step already focused one of its own fields.
 */
export const StepFocus = (): null => {
  const pathname = usePathname();
  // Compare against the last pathname instead of a first-render flag: Strict Mode runs effects twice on mount
  const previousPathname = useRef(pathname);

  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    const current = document.querySelector<HTMLElement>(HEADING_SELECTOR);
    if (current) {
      focusHeading(current);
      return;
    }
    // Step content can mount after the route commits (code-split / client-checked steps)
    const observer = new MutationObserver(() => {
      const heading = document.querySelector<HTMLElement>(HEADING_SELECTOR);
      if (heading) {
        observer.disconnect();
        focusHeading(heading);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timeout = window.setTimeout(() => observer.disconnect(), WAIT_FOR_HEADING_MS);
    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [pathname]);

  return null;
};
